import { merge } from "theme-ui"
import tailwind from "@theme-ui/preset-tailwind"
import { lightThemeVars, darkThemeVars } from "@lekoarts/gatsby-theme-minimal-blog/src/utils/prism-themes"

const theme = merge(tailwind, {
  config: {
    initialColorModeName: `light`,
    useColorSchemeMediaQuery: false,
  },
  space: [
    "0",
    "0.25rem",
    "0.5rem",
    "1rem",
    "2rem",
    "3rem",
    "4.5rem",
    "7rem",
    "32rem",
  ],
  colors: {
    text: `#1A1A18`,
    background: `#FAF9F7`,
    primary: `#C2522D`,
    secondary: `#6B6B63`,
    accent: `#C2522D`,
    toggleIcon: tailwind.colors.gray[8],
    heading: `#1A1A18`,
    divide: `#D8D4CC`,
    muted: `#F2F0EC`,
    highlightLineBg: `rgba(194, 82, 45, 0.06)`,
    emphasisBg: `#1B1914`,
    emphasisText: `#EDE8DD`,
    ...lightThemeVars,
    modes: {
      dark: {
        text: `#1A1A18`,
        background: `#FAF9F7`,
        primary: `#C2522D`,
        secondary: `#6B6B63`,
        accent: `#C2522D`,
        toggleIcon: tailwind.colors.gray[8],
        heading: `#1A1A18`,
        divide: `#D8D4CC`,
        muted: `#F2F0EC`,
        highlightLineBg: `rgba(194, 82, 45, 0.06)`,
        emphasisBg: `#1B1914`,
        emphasisText: `#EDE8DD`,
        ...darkThemeVars,
      },
    },
  },
  fonts: {
    body: `'Styrene A', 'Styrene B', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`,
    heading: `'Styrene A', 'Styrene B', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`,
    monospace: `'JetBrains Mono', 'Fira Code', 'Cascadia Code', Consolas, 'Courier New', monospace`,
    serif: `Fraunces, Georgia, serif`,
  },
  fontWeights: {
    body: 400,
    heading: 500,
    bold: 600,
  },
  lineHeights: {
    body: 1.7,
    heading: 1.2,
  },
  letterSpacings: {
    heading: `-0.01em`,
  },
  styles: {
    root: {
      color: `text`,
      backgroundColor: `background`,
      backgroundImage: `radial-gradient(circle at top left, rgba(212, 165, 116, 0.12), transparent 24%), linear-gradient(180deg, rgba(255,255,255,0.45), rgba(255,255,255,0))`,
      margin: 0,
      padding: 0,
      boxSizing: `border-box`,
      textRendering: `optimizeLegibility`,
      WebkitFontSmoothing: `antialiased`,
      MozOsxFontSmoothing: `grayscale`,
      WebkitTextSizeAdjust: `100%`,
      img: {
        borderStyle: `none`,
      },
      pre: {
        fontFamily: `monospace`,
        fontSize: `0.9em`,
        backgroundColor: `muted`,
        borderRadius: `6px`,
        border: `1px solid`,
        borderColor: `divide`,
        color: `heading`,
        lineHeight: 1.65,
        boxShadow: `0 1px 2px rgba(26, 26, 24, 0.06)`,
        overflowX: `auto`,
        p: 3,
        my: 4,
        code: {
          backgroundColor: `transparent`,
          borderRadius: 0,
          color: `inherit`,
          display: `block`,
          fontSize: `inherit`,
          lineHeight: `inherit`,
          p: 0,
          whiteSpace: `pre`,
        },
      },
      a: {
        transition: `color 0.2s ease`,
        color: `primary`,
        textDecorationColor: `divide`,
        ":hover": {
          color: `#A8421F`,
        },
      },
      "*:focus-visible": {
        outline: `2px solid #C2522D`,
        outlineOffset: `2px`,
      },
    },
    p: {
      fontSize: [`17px`, `17px`, `17px`],
      letterSpacing: `-0.01em`,
      lineHeight: `body`,
      "--baseline-multiplier": 0.179,
      "--x-height-multiplier": 0.35,
      wordBreak: `break-word`,
    },
    ul: {
      pl: `1.2rem`,
      my: 3,
      li: {
        fontSize: [`17px`, `17px`, `17px`],
        letterSpacing: `-0.008em`,
        lineHeight: `body`,
        mb: 2,
        pl: `0.2rem`,
      },
    },
    ol: {
      pl: `1.35rem`,
      my: 3,
      li: {
        fontSize: [`17px`, `17px`, `17px`],
        letterSpacing: `-0.008em`,
        lineHeight: `body`,
        mb: 2,
        pl: `0.2rem`,
      },
    },
    h1: {
      variant: `text.heading`,
      fontSize: [`2.75rem`, `3.5rem`, `4rem`, `4.5rem`],
      fontWeight: 500,
      mt: 4,
      lineHeight: 1.08,
      letterSpacing: `-0.03em`,
    },
    h2: {
      variant: `text.heading`,
      fontSize: [`2rem`, `2.3rem`, `2.5rem`, `2.8rem`],
      mt: 5,
      mb: 2,
      fontWeight: 500,
      letterSpacing: `-0.03em`,
    },
    h3: {
      variant: `text.heading`,
      fontSize: [`1.35rem`, `1.5rem`, `1.65rem`, `1.8rem`],
      mt: 4,
      mb: 2,
      fontWeight: 500,
      letterSpacing: `-0.025em`,
    },
    h4: {
      variant: `text.heading`,
      fontSize: [1, 2, 2, 3],
      mt: 3,
      letterSpacing: `-0.004em`,
    },
    h5: {
      variant: `text.heading`,
      fontSize: [1, 2],
      mt: 3,
    },
    h6: {
      variant: `text.heading`,
      fontSize: 1,
      mb: 2,
    },
    blockquote: {
      borderLeftColor: `accent`,
      borderLeftStyle: `solid`,
      borderLeftWidth: `3px`,
      mx: 0,
      pl: 4,
      py: 1,
      color: `secondary`,
      fontStyle: `italic`,
      backgroundColor: `#F2F0EC`,
      borderRadius: `0 12px 12px 0`,
      p: {
        fontStyle: `italic`,
        my: 0,
      },
    },
    table: {
      width: `100%`,
      my: 4,
      borderCollapse: `separate`,
      borderSpacing: 0,
      fontSize: [1, 1, 2],
      th: {
        textAlign: `left`,
        py: `4px`,
        pr: `4px`,
        pl: 0,
        borderColor: `divide`,
        borderBottomStyle: `solid`,
      },
      td: {
        textAlign: `left`,
        py: `4px`,
        pr: `4px`,
        pl: 0,
        borderColor: `divide`,
        borderBottomStyle: `solid`,
      },
    },
    th: {
      verticalAlign: `bottom`,
      borderBottomWidth: `2px`,
      color: `heading`,
      fontWeight: 600,
    },
    td: {
      verticalAlign: `top`,
      borderBottomWidth: `1px`,
    },
    hr: {
      mx: 0,
      borderColor: `divide`,
    },
    img: {
      borderRadius: `6px`,
      border: `1px solid`,
      borderColor: `divide`,
      boxShadow: `0 1px 2px rgba(26,26,24,0.06)`,
      maxWidth: `100%`,
    },
    code: {
      fontFamily: `monospace`,
      fontSize: `0.875em`,
      backgroundColor: `#F0EDE8`,
      borderRadius: `6px`,
      px: `0.4em`,
      py: `0.15em`,
      color: `heading`,
    },
    inlineCode: {
      fontFamily: `monospace`,
      fontSize: `0.875em`,
      backgroundColor: `#F0EDE8`,
      borderRadius: `6px`,
      px: `0.4em`,
      py: `0.15em`,
      color: `heading`,
    },
  },
  layout: {
    container: {
      padding: [3, 4, 5],
      maxWidth: `1380px`,
    },
    content: {
      figure: {
        margin: 0,
        img: {
          borderRadius: `6px`,
          maxWidth: `100%`,
        },
      },
    },
  },
  text: {
    heading: {
      fontFamily: `heading`,
      fontWeight: `heading`,
      lineHeight: `heading`,
      color: `heading`,
    },
  },
  copyButton: {
    backgroundColor: `muted`,
    border: `none`,
    color: `secondary`,
    cursor: `pointer`,
    fontSize: [`12px`, `12px`, `13px`],
    fontFamily: `monospace`,
    letterSpacing: `0.01em`,
    transition: `all 0.2s ease`,
    "&[disabled]": {
      cursor: `not-allowed`,
    },
    ":not([disabled]):hover": {
      bg: `primary`,
      color: `white`,
    },
    position: `absolute`,
    right: 0,
    zIndex: 1,
    borderRadius: `0 0 0 4px`,
    padding: `0.2rem 0.5rem`,
  },
  dividers: {
    bottom: {
      borderBottomStyle: `solid`,
      borderBottomWidth: `1px`,
      borderBottomColor: `divide`,
      pb: 3,
    },
    top: {
      borderTopStyle: `solid`,
      borderTopWidth: `1px`,
      borderTopColor: `divide`,
      pt: 3,
    },
  },
  links: {
    secondary: {
      color: `secondary`,
      textDecoration: `none`,
      ":hover": {
        color: `accent`,
        textDecoration: `none`,
      },
      ":focus": {
        color: `accent`,
      },
    },
    listItem: {
      fontSize: [1, 2, 3],
      color: `text`,
      fontWeight: 500,
      ":hover": {
        color: `accent`,
      },
    },
  },
  buttons: {
    primary: {
      fontFamily: `body`,
      fontWeight: `bold`,
      backgroundColor: `accent`,
      color: `background`,
      borderRadius: `8px`,
      border: `none`,
      cursor: `pointer`,
      transition: `opacity 0.2s ease`,
      ":hover": {
        opacity: 0.9,
      },
    },
  },
  cards: {
    primary: {
      backgroundColor: `muted`,
      border: `1px solid`,
      borderColor: `divide`,
      borderRadius: `12px`,
    },
  },
})

export default theme
